import type { UseQueryResult } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { Sync, SyncsResponse } from "@/api/queries";

import { useGetMyAccount, useGetMyProfile } from "@/api/queries";
import useSiteConfigAuth from "@/api/sanity/queries/getSiteConfigWithCode";
import useSyncsQuery from "@/api/sanity/queries/getSyncs";
import useAppStore from "@/store/appStateZustand";

import type { SyncsApplyFormData } from "../utils/syncsApplySchema";

import { createSyncsApplySchema } from "../utils/syncsApplySchema";
import useWorkSearch from "./useWorkSearch";

type SyncsAvailableData = { syncs_available?: SyncsResponse };

export function useSyncsApplyForm() {
  const { t } = useTranslation();
  const { syncId } = useParams({
    strict: false,
    select: (p): { syncId: string | undefined } => ({
      syncId: (p as Record<string, string | undefined>).syncId,
    }),
  });

  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const allSyncsAvailable = useAppStore(s => s.allSyncsAvailable) as unknown as boolean;

  const { options, cachedSongs, handleInputChange } = useWorkSearch(selectedAccount);

  const myAccount = useGetMyAccount({ artistaccountcode: selectedAccount });
  const userInfo = useGetMyProfile();

  const { data: siteConfig } = useSiteConfigAuth();
  const noSyncs = siteConfig?.disable_syncs;

  const syncsQuery = useSyncsQuery() as UseQueryResult<
    SyncsResponse | SyncsAvailableData,
    Error
  >;
  const { isSuccess } = syncsQuery;

  const syncsRaw = syncsQuery.data;
  const syncsData: Sync[] = allSyncsAvailable === true
    ? (syncsRaw as SyncsResponse | undefined) ?? []
    : (syncsRaw as SyncsAvailableData | undefined)?.syncs_available ?? [];

  const [pending, setPending] = useState<boolean>(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState<string>("");

  const form = useForm<SyncsApplyFormData>({
    resolver: zodResolver(createSyncsApplySchema(t)),
    defaultValues: {
      opp: syncId ?? "",
      songList: [],
      content: "",
    },
  });

  return {
    form,
    selectedAccount,
    myAccount,
    userInfo,
    syncsData,
    isSuccess,
    noSyncs,
    pending,
    setPending,
    mutationErrorMessage,
    setMutationErrorMessage,
    options,
    cachedSongs,
    handleInputChange,
  };
}
